import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Sustainable design principles applied:
// - Minimal color palette
// - Simple, lightweight animations
// - Optimized image loading
// - Reduced JavaScript usage
// - Clean, semantic HTML structure

const ECarbonSection = styled.section`
  min-height: 100vh;
  background: #f5f8f5;
  color: #333;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.header`
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const Logo = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2a7d4f;
  margin: 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 2rem;
`;

const NavLink = styled(ScrollLink)`
  color: #333;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #2a7d4f;
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const StyledRouterLink = styled(RouterLink)`
  text-decoration: none;
  color: inherit;
`;

const Hero = styled.div`
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 5%;
  background-color: #f5f8f5;
  position: relative;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 2rem;
    text-align: center;
    gap: 1rem;
  }
`;

const HeroContent = styled.div`
  z-index: 2;
  max-width: 550px;
  text-align: left;
  
  @media (max-width: 768px) {
    max-width: 100%;
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const HeroImage = styled.div`
  width: 45%;
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
  }
`;

const HeroTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #2a7d4f;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  max-width: 700px;
  margin-bottom: 2rem;
  color: #333;
  line-height: 1.6;
`;

const Section = styled.div`
  padding: 5rem 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  color: #2a7d4f;
  
  &:after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background: #2a7d4f;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeamMember = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const MemberImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border: 3px solid #2a7d4f;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MemberName = styled.h3`
  font-size: 1.3rem;
  margin: 0.5rem 0;
  color: #2a7d4f;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #1a5f3f;
  }
`;

const MemberRole = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const MemberBio = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: #555;
`;

const Feature = styled(motion.div)`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
  display: flex;
  flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
  min-width: 300px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const FeatureImage = styled.div`
  flex: 1;
  min-width: 300px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.03);
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
    margin-top: 1.5rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2a7d4f;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &:before {
    content: '';
    position: absolute;
    width: 2px;
    background: #2a7d4f;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -1px;
  }
  
  @media (max-width: 768px) {
    &:before {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  padding: 10px 40px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
  margin-bottom: 2rem;
  
  ${props => props.position === 'left' ? `
    left: 0;
  ` : `
    left: 50%;
  `}
  
  &:after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background: #2a7d4f;
    border-radius: 50%;
    top: 15px;
    
    ${props => props.position === 'left' ? `
      right: -10px;
    ` : `
      left: -10px;
    `}
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 70px;
    padding-right: 20px;
    left: 0;
    
    &:after {
      left: 20px;
      right: auto;
    }
  }
`;

const TimelineContent = styled.div`
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const TimelineDate = styled.span`
  font-weight: bold;
  color: #2a7d4f;
  display: block;
  margin-bottom: 0.5rem;
`;

const FormContainer = styled.form`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border-radius: 5px;
  border: 1px solid #ddd;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2a7d4f;
  }
`;

const SubmitButton = styled.button`
  background: #2a7d4f;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: #1e5d3a;
  }
`;

const Footer = styled.footer`
  background: #333;
  color: white;
  padding: 2rem;
  text-align: center;
`;

const FormStatus = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 5px;
  text-align: center;
  
  ${props => props.type === 'error' && `
    background-color: #fee2e2;
    color: #dc2626;
  `}
  
  ${props => props.type === 'success' && `
    background-color: #dcfce7;
    color: #16a34a;
  `}
  
  ${props => props.type === 'loading' && `
    background-color: #f3f4f6;
    color: #4b5563;
  `}
`;

const ECarbon = () => {
  const productRef = useRef(null);
  const timelineRef = useRef(null);

  const teamMembers = [
    {
      name: "Byeongsu Ko",
      role: "CEO",
      bio: "Computer Engineering, Dong-A University. Oversees carbon measurement engine and overall product architecture design. Directly develops carbon API, backend systems, and performance optimization. Proven technical leadership through multiple awards (startup competitions/environmental SW competitions).",
      image: `${process.env.PUBLIC_URL}/images/byeongsuko.png`
    },
    {
      name: "Donggyu Han",
      role: "CTO",
      bio: "Computer Engineering, Dong-A University. Implements resource optimization logic for eco-friendly web development. Introduced and improved front-end performance measurement tools. Received excellent evaluation in the UX section at Seoul Environmental SW Competition.",
      image: `${process.env.PUBLIC_URL}/images/dongkyuhan.jpg`
    },
    {
      name: "Mingyu Kang",
      role: "Backend Developer",
      bio: "Computer Engineering, Dong-A University. Created APIs based on carbon emission calculation formulas. Experience in AWS-based server operations (EC2, Lambda). Participated in the technical implementation part of the Startup Challenge.",
      image: `${process.env.PUBLIC_URL}/images/minkyukang.jpg`
    }
  ];

  const features = [
    {
      title: "Carbon Emission Analysis",
      description: "Our advanced tools accurately measure your website's carbon footprint, analyzing everything from server energy to data transfer.",
      image: `${process.env.PUBLIC_URL}/images/carbon-analysis.png`
    },
    {
      title: "W3C Sustainability Guidelines Evaluation",
      description: "We evaluate your website against the W3C's sustainability guidelines, providing actionable insights for improvement.",
      image: `${process.env.PUBLIC_URL}/images/carbon-analysis-second.png`
    },
    {
      title: "W3C Sustainability Guidelines Evaluation",
      description: "We evaluate your website against the W3C's sustainability guidelines, providing actionable insights for improvement.",
      image: `${process.env.PUBLIC_URL}/images/w3c-analysis.png`
    },
    {
      title: "Code & Image Optimization Recommendations",
      description: "Receive AI-powered recommendations to optimize your code and images, reducing your website's carbon footprint while maintaining performance.",
      image: `${process.env.PUBLIC_URL}/images/optimization-ai.png`
    }
  ];

  const timelineEvents = [
    {
      date: "April 2025",
      title: "Company Founded",
      description: "eCarbon was founded with a mission to make the web more sustainable.",
      position: "right"
    },
    {
      date: "April 2025",
      title: "MVP Launch",
      description: "Released our minimum viable product to select partners.",
      position: "left"
    },
    {
      date: "April 2025",
      title: "Busan Pre-startup Package Selection",
      description: "Selected for the Busan Pre-startup Package, providing entrepreneurship education and technical IP acquisition.",
      position: "right"
    }
  ];

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    organization: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: 'loading', message: 'Submitting your request...' });

    try {
      const { data, error } = await supabase
        .from('demo_requests')
        .insert([
          {
            email: formData.email,
            name: formData.name,
            organization: formData.organization,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) throw error;

      setFormStatus({ 
        type: 'success', 
        message: 'Thank you for your interest! We will contact you soon.' 
      });
      setFormData({ email: '', name: '', organization: '' });
      
    } catch (error) {
      console.error('Error submitting demo request:', error);
      setFormStatus({ 
        type: 'error', 
        message: 'There was an error submitting your request. Please try again.' 
      });
    }
  };

  return (
    <ECarbonSection>
      <Header>
        <Logo>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="#2a7d4f"/>
            <path d="M12 7C9.24 7 7 9.24 7 12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12C17 9.24 14.76 7 12 7ZM12 15C10.35 15 9 13.65 9 12C9 10.35 10.35 9 12 9C13.65 9 15 10.35 15 12C15 13.65 13.65 15 12 15Z" fill="#2a7d4f"/>
            <path d="M12 11C11.45 11 11 11.45 11 12C11 12.55 11.45 13 12 13C12.55 13 13 12.55 13 12C13 11.45 12.55 11 12 11Z" fill="#2a7d4f"/>
          </svg>
          eCarbon
        </Logo>
        <Nav>
          <NavLink to="product" smooth={true} duration={500}>Product</NavLink>
          <NavLink to="timeline" smooth={true} duration={500}>Timeline</NavLink>
        </Nav>
      </Header>

      <Hero>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Website Carbon Measurement & Evaluation Platform
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Helping businesses reduce their digital carbon footprint through accurate measurement and W3C sustainability guideline evaluation.
          </HeroSubtitle>
        </HeroContent>
        <HeroImage>
          <motion.img 
            src={`${process.env.PUBLIC_URL}/images/hero_section_svg.svg`} 
            alt="eCarbon Dashboard Illustration" 
            loading="lazy"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          />
        </HeroImage>
      </Hero>

      <Section>
        <SectionTitle>Our Team</SectionTitle>
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMember
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <MemberImage>
                <img src={member.image} alt={member.name} loading="lazy" />
              </MemberImage>
              {member.name === "Byeongsu Ko" ? (
                <StyledRouterLink to="/about_byeongsuko">
                  <MemberName>{member.name}</MemberName>
                </StyledRouterLink>
              ) : (
                <MemberName>{member.name}</MemberName>
              )}
              <MemberRole>{member.role}</MemberRole>
              <MemberBio>{member.bio}</MemberBio>
            </TeamMember>
          ))}
        </TeamGrid>
      </Section>

      <Section id="product" ref={productRef}>
        <SectionTitle>Our Product</SectionTitle>
        {features.map((feature, index) => (
          <Feature
            key={index}
            reverse={index % 2 !== 0}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <FeatureContent>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureContent>
            <FeatureImage>
              <img 
                src={feature.image} 
                alt={feature.title} 
                loading="lazy"
              />
            </FeatureImage>
          </Feature>
        ))}
      </Section>

      <Section id="timeline" ref={timelineRef}>
        <SectionTitle>Our Journey</SectionTitle>
        <Timeline>
          {timelineEvents.map((event, index) => (
            <TimelineItem
              key={index}
              position={event.position}
              initial={{ opacity: 0, x: event.position === 'left' ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <TimelineContent>
                <TimelineDate>{event.date}</TimelineDate>
                <h3>{event.title}</h3>
                <p>{event.description}</p>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Section>

      <Section>
        <SectionTitle>Request a Demo</SectionTitle>
        <FormContainer onSubmit={handleSubmit}>
          {formStatus.message && (
            <FormStatus type={formStatus.type}>
              {formStatus.message}
            </FormStatus>
          )}
          <FormGroup>
            <FormLabel htmlFor="email">Email:</FormLabel>
            <FormInput
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="name">Name:</FormLabel>
            <FormInput
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="organization">Organization:</FormLabel>
            <FormInput
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <SubmitButton 
            type="submit" 
            disabled={formStatus.type === 'loading'}
          >
            {formStatus.type === 'loading' ? 'Submitting...' : 'Request Demo'}
          </SubmitButton>
        </FormContainer>
      </Section>

      <Footer>
        <p> {new Date().getFullYear()} eCarbon. All rights reserved.</p>
        <p>Designed with sustainability in mind.</p>
      </Footer>
    </ECarbonSection>
  );
};

export default ECarbon;
